import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../auth/AuthProvider.jsx";
import { fetchNotifications, markNotificationsRead } from "./api.js";

// Как часто перепроверяем ленту: оплата подтверждается за секунды,
// но опрашивать чаще незачем — это фон, а не чат.
const POLL_INTERVAL_MS = 60_000;

const NotificationsContext = createContext({
  items: [],
  unreadCount: 0,
  status: "loading",
  error: "",
  refresh: () => {},
  markAllRead: () => {},
});

export function NotificationsProvider({ children }) {
  const { getIdToken } = useAuth();
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const apply = useCallback((payload) => {
    if (!mountedRef.current || !payload) return;
    setItems(Array.isArray(payload.items) ? payload.items : []);
    setUnreadCount(Number.isFinite(payload.unreadCount) ? payload.unreadCount : 0);
    setStatus("ready");
    setError("");
  }, []);

  const refresh = useCallback(async () => {
    try {
      const token = await getIdToken();
      if (!token) return;
      apply(await fetchNotifications(token));
    } catch (cause) {
      if (!mountedRef.current) return;
      // Молчаливая лента лучше, чем баннер об ошибке поверх работы:
      // покажем сбой только если не удалось загрузить ни разу.
      setStatus((previous) => (previous === "loading" ? "error" : previous));
      setError(cause?.message || "Не получилось загрузить уведомления.");
    }
  }, [apply, getIdToken]);

  const markAllRead = useCallback(async () => {
    const unread = items.filter((item) => !item.readAt);
    if (!unread.length) return;
    setUnreadCount(0);
    setItems((previous) => previous.map((item) => (
      item.readAt ? item : { ...item, readAt: new Date().toISOString() }
    )));
    try {
      const token = await getIdToken();
      if (!token) return;
      apply(await markNotificationsRead(token, unread.map((item) => item.id)));
    } catch {
      refresh();
    }
  }, [apply, getIdToken, items, refresh]);

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, POLL_INTERVAL_MS);
    const onFocus = () => refresh();
    globalThis.addEventListener?.("focus", onFocus);
    return () => {
      clearInterval(timer);
      globalThis.removeEventListener?.("focus", onFocus);
    };
  }, [refresh]);

  const value = useMemo(
    () => ({ items, unreadCount, status, error, refresh, markAllRead }),
    [error, items, markAllRead, refresh, status, unreadCount],
  );

  return (
    <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>
  );
}

export function useNotifications() {
  return useContext(NotificationsContext);
}
